// Dev uses the Vite proxy (same-origin, no CORS); production builds bake in the
// absolute API origin from VITE_API_BASE_URL.
const BASE = import.meta.env.VITE_API_BASE_URL ?? '/v1';

const ACCESS_KEY = 'parkingpay.web.access';
const REFRESH_KEY = 'parkingpay.web.refresh';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: Record<string, unknown>,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  get isOffline() {
    return this.status === 0;
  }
  get isQuoteExpired() {
    return (
      this.code === 'quote_expired' || this.code === 'quote_invalidated' || this.code === 'quote_consumed'
    );
  }
  get isUnauthorized() {
    return this.status === 401;
  }
}

/**
 * Tokens live in localStorage.
 *
 * A browser has no keystore, so this is the honest ceiling of what a web client
 * can do — which is exactly why the access token is short-lived and every
 * money-moving call is re-authorised server-side against a fresh quote.
 */
export const tokens = {
  get access() {
    return localStorage.getItem(ACCESS_KEY);
  },
  get refresh() {
    return localStorage.getItem(REFRESH_KEY);
  },
  save(next: { accessToken: string; refreshToken: string }) {
    localStorage.setItem(ACCESS_KEY, next.accessToken);
    localStorage.setItem(REFRESH_KEY, next.refreshToken);
  },
  clear() {
    localStorage.removeItem(ACCESS_KEY);
    localStorage.removeItem(REFRESH_KEY);
  },
  get signedIn() {
    return Boolean(localStorage.getItem(ACCESS_KEY));
  },
};

let refreshInFlight: Promise<boolean> | null = null;

async function refresh(): Promise<boolean> {
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = (async () => {
    const token = tokens.refresh;
    if (!token) return false;
    try {
      const response = await fetch(`${BASE}/auth/refresh`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ refreshToken: token }),
      });
      if (!response.ok) {
        tokens.clear();
        return false;
      }
      tokens.save(await response.json());
      return true;
    } catch {
      return false;
    } finally {
      refreshInFlight = null;
    }
  })();
  return refreshInFlight;
}

interface Options {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  body?: unknown;
  headers?: Record<string, string>;
  signal?: AbortSignal;
}

async function request<T>(path: string, options: Options = {}, retry = true): Promise<T> {
  const token = tokens.access;
  let response: Response;
  try {
    response = await fetch(`${BASE}${path}`, {
      method: options.method ?? 'GET',
      signal: options.signal,
      headers: {
        'content-type': 'application/json',
        ...(token ? { authorization: `Bearer ${token}` } : {}),
        ...options.headers,
      },
      ...(options.body === undefined ? {} : { body: JSON.stringify(options.body) }),
    });
  } catch {
    // Underground garages drop the connection constantly. The service worker
    // may still answer lot/price reads from cache; anything else surfaces a
    // retryable offline state rather than an exception the UI cannot explain.
    throw new ApiError(0, 'network_error', 'Network request failed');
  }

  if (response.status === 401 && retry && (await refresh())) {
    return request<T>(path, options, false);
  }

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  const payload = text ? (JSON.parse(text) as unknown) : null;

  if (!response.ok) {
    const error = (
      payload as { error?: { code: string; message: string; details?: Record<string, unknown> } }
    )?.error;
    throw new ApiError(
      response.status,
      error?.code ?? 'unknown_error',
      error?.message ?? `Request failed with status ${response.status}`,
      error?.details,
    );
  }
  return payload as T;
}

export const api = {
  get: <T>(path: string, signal?: AbortSignal) => request<T>(path, { signal }),
  post: <T>(path: string, body?: unknown, headers?: Record<string, string>) =>
    request<T>(path, { method: 'POST', body, headers }),
  patch: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PATCH', body }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
};

/**
 * Makes sure the caller has *some* identity before a write.
 *
 * A first-time driver gets an anonymous account created silently — paying must
 * not require signing up. That account lives only in this browser's storage,
 * which is why the success screen offers to bind an email to it.
 */
let ensuring: Promise<void> | null = null;

export async function ensureIdentity(): Promise<void> {
  if (tokens.signedIn) return;
  if (ensuring) return ensuring;
  ensuring = (async () => {
    try {
      const session = await request<{ accessToken: string; refreshToken: string }>('/auth/guest', {
        method: 'POST',
      });
      tokens.save(session);
    } finally {
      ensuring = null;
    }
  })();
  return ensuring;
}

/** One key per payment attempt, reused across every retry (FR-U-29). */
export function newIdempotencyKey(): string {
  return crypto.randomUUID();
}
